<template>
  <a-modal
    :open="open"
    :width="456"
    :closable="false"
    :maskClosable="false"
    centered
    @cancel="handleCancel"
  >
    <div class="modal-body">
      <div class="icon-wrapper">
        <QuestionCircleOutlined class="icon" />
      </div>

      <div class="content">
        <a-spin :spinning="loading" v-if="contact || contactsIds.length > 0">
          <div>
            <h3 class="title">
              {{ title }}
            </h3>
            <p class="description">
              {{ description }}
            </p>
            <p class="subtitle" v-if="columns.length > 0">
              {{ subtitle }}  
            </p>
            <ul class="columns-list" v-if="columns.length > 0">
              <li v-for="column in columns" :key="column">
                {{ column?.title }}
              </li>
            </ul>
          </div>
        </a-spin>
      </div>
    </div>

    <template #footer>
      <div class="footer">
        <a-button @click="handleCancel">
          No, cancelar
        </a-button>
        <a-button type="primary" :loading="loadingSave" :disabled="loadingSave" @click="handleConfirm">
          Sí, exportar
        </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { QuestionCircleOutlined } from "@ant-design/icons-vue";
import { getHomologatedFields } from "@/app/contacts/services";

interface Props {
  open: boolean;
  contactName: string;
  contactsIds: any[];
  contact: any;
  type: string;
  loadingSave: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "onCancel"): void;
  (e: "onConfirm"): void;
}>();

const columns = ref<any[]>([]);

const handleCancel = () => {
  emit("onCancel");
};

const handleConfirm = () => {
  emit("onConfirm");
};

const loading = ref(false);

const title = computed(() => {
  const isSingle = !!props.contact;
  const count = props.contactsIds ? props.contactsIds?.length : 1;
  const plural = count > 1 ? 'contactos' : 'contacto';
  const hasColumns = columns.value.length > 0;

  const name = props.contact?.fullName;
  const campaignText = hasColumns ? '' : ' de diferentes campañas';

  // Configuración por tipo
  const config = {
    'export': {
      single: `¿Deseas exportar el contacto “${name}” a HubSpot?`,
      bulk: `Estás a punto de exportar ${count} contactos${campaignText} a HubSpot, ¿deseas continuar?`,
    },
    'update-export': {
      single: `¿Deseas actualizar el contacto “${name}” con HubSpot?`,
      bulk: `Estás a punto de actualizar ${count} ${plural}${campaignText} con HubSpot, ¿deseas continuar?`,
    },
    'import': {
      single: `¿Deseas actualizar el contacto importado “${name}” en BeexCC?`,
      bulk: `Estás a punto de actualizar ${count} ${plural} con HubSpot, ¿deseas continuar?`,
    },
  };

  const type = props.type === 'export' || props.type === 'update-export'
    ? props.type
    : 'import';

  return isSingle ? config[type].single : config[type].bulk;
});

const description = computed(() => {
  if (props.type === "export") {
    if (columns.value.length === 0) {
      return `Recuerda que solo se exportarán a HubSpot los siguientes datos de BeexCC.
              Esta acción no se podrá detener o deshacer.`;
    } else {
      return `Recuerda que solo se exportarán los datos de las columnas homologadas y
              el teléfono más reciente. La exportación será a una misma cuenta de Hubspot.`;
    }
  } else if (props.type === "update-export") {
    if (props.contact) {
      return `Al actualizar, se exportarán los siguientes datos desde BeexCC:`;
    } else {
      if (columns.value.length === 0) {
        return `Se actualizarán los siguientes datos desde BeexCC, segun corresponda por empresa y campaña`;
      } else {
        return `Recuerda que solo se actualizarán los datos de las columnas homologadas con Hubspot y
              el teléfono según corresponda por empresa y campaña.`;
      }
    }
  } else {
    if (props.contact) {
      return `Al actualizar, se importarán los siguientes datos desde Hubspot:`;
    } else {
      if (columns.value.length === 0) {
        return `Se actualizarán los siguientes datos desde BeexCC, según corresponda por empresa y campaña`;
      } else {
        return `Recuerda que solo se actualizarán los datos de las columnas homologadas con Hubspot y
              el teléfono, según corresponda por empresa y campaña.`;
      }
    }
  }
});

const subtitle = computed(() => {
  if (props.type === "export") {
    return `Datos a exportar (${columns.value.length})`;
  } else {
    return `Datos del contacto (${columns.value.length})`;
  }
});

const fetchColumns = async () => {
  columns.value = [];

  const contactsIds = props.contactsIds ? props.contactsIds : [props.contact?.id];
  if (!contactsIds) return;

  loading.value = true;
  try {
    const response = await getHomologatedFields(contactsIds);
    columns.value = response.columns || [];
  } finally {
    loading.value = false;
  }
};


watch(
  () => props.open,
  (isOpen) => {
    console.log("Modal open changed:", isOpen);
    if (isOpen) fetchColumns();
    else columns.value = [];
  },
  { immediate: true } // útil para que corra al montar si ya viene true
);


</script>

<style scoped lang="scss">
:deep(.ant-modal-content) {
  border-radius: 16px;
}

.modal-body {
  display: flex;
  gap: 16px;
}

.icon-wrapper {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff7e6;
}

.icon {
  font-size: 22px;
  color: #faad14;
}

.content {
  flex: 1;
}

.title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: #262626E0;
}

.description {
  margin: 0 0 16px;
  color: #262626E0;
}

.subtitle {
  margin: 0 0 4px;
  font-weight: 600;
  color: #262626E0;
}

.columns-list {
  margin: 0;
  padding-left: 18px;
  color: #262626E0;
}

.footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
